import { useState, useEffect } from 'react';
import { X, Building2, User, Mail, Phone, Globe, MapPin, Wrench, Shield, CreditCard, Loader2, Send, Key } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../ui/utils';
import { vendorService } from '../../utils/vendors/vendorService';
import { Vendor } from '../../types/vendor';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '../ui/dialog';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface VendorDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    vendor?: Vendor | null;
    currentUserId: string;
    currentUserName: string;
    isDarkMode: boolean;
}

export function VendorDialog({
    isOpen,
    onClose,
    onSuccess,
    vendor,
    currentUserId,
    currentUserName,
    isDarkMode
}: VendorDialogProps) {
    const [loading, setLoading] = useState(false);
    const [sendingInvite, setSendingInvite] = useState(false);
    const [step, setStep] = useState<'details' | 'invite'>('details');
    
    const [formData, setFormData] = useState({
        name: '',
        dbaName: '',
        contactName: '',
        contactTitle: '',
        email: '',
        phone: '',
        alternatePhone: '',
        website: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        services: [] as string[],
        otherServiceDescription: '',
        serviceAreaRadius: 50,
        offers24_7Emergency: false,
        emergencyPhone: '',
        paymentTerms: 'net_30' as 'net_15' | 'net_30' | 'net_45' | 'net_60' | 'due_on_receipt',
        preferredPaymentMethod: 'check' as 'check' | 'ach' | 'wire' | 'credit_card',
        notes: '',
    });

    const serviceOptions = [
        'Pump Repair', 'Tank Maintenance', 'Electrical', 'Plumbing', 'HVAC',
        'Welding', 'Trucking/Hauling', 'Water Hauling', 'Swabbing', 'Well Servicing',
        'Pressure Washing', 'Inspection Services', 'Environmental', 'Other'
    ];

    useEffect(() => {
        if (isOpen) {
            if (vendor) {
                setFormData({
                    name: vendor.name || '',
                    dbaName: vendor.dbaName || '',
                    contactName: vendor.contactName || '',
                    contactTitle: vendor.contactTitle || '',
                    email: vendor.email || '',
                    phone: vendor.phone || '',
                    alternatePhone: vendor.alternatePhone || '',
                    website: vendor.website || '',
                    street: vendor.physicalAddress?.street || '',
                    city: vendor.physicalAddress?.city || '',
                    state: vendor.physicalAddress?.state || '',
                    zip: vendor.physicalAddress?.zip || '',
                    services: vendor.services || [],
                    otherServiceDescription: vendor.otherServiceDescription || '',
                    serviceAreaRadius: vendor.serviceAreaRadius || 50,
                    offers24_7Emergency: vendor.offers24_7Emergency || false,
                    emergencyPhone: vendor.emergencyPhone || '',
                    paymentTerms: vendor.paymentTerms || 'net_30',
                    preferredPaymentMethod: vendor.preferredPaymentMethod || 'check',
                    notes: vendor.notes || '',
                });
                setStep('details');
            } else {
                setFormData({
                    name: '', dbaName: '', contactName: '', contactTitle: '',
                    email: '', phone: '', alternatePhone: '', website: '',
                    street: '', city: '', state: '', zip: '',
                    services: [], otherServiceDescription: '', serviceAreaRadius: 50,
                    offers24_7Emergency: false, emergencyPhone: '',
                    paymentTerms: 'net_30', preferredPaymentMethod: 'check', notes: '',
                });
                setStep('details');
            }
        }
    }, [isOpen, vendor]);

    const handleServiceToggle = (service: string) => {
        setFormData(prev => ({
            ...prev,
            services: prev.services.includes(service)
                ? prev.services.filter(s => s !== service)
                : [...prev.services, service]
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name || !formData.email) {
            toast.error('Company Name and Email are required');
            return;
        }

        try {
            setLoading(true);
            const vendorData = {
                name: formData.name,
                dbaName: formData.dbaName,
                contactName: formData.contactName,
                contactTitle: formData.contactTitle,
                email: formData.email,
                phone: formData.phone,
                alternatePhone: formData.alternatePhone,
                website: formData.website,
                physicalAddress: { street: formData.street, city: formData.city, state: formData.state, zip: formData.zip, country: 'USA' },
                mailingAddress: { street: formData.street, city: formData.city, state: formData.state, zip: formData.zip, country: 'USA' },
                services: formData.services,
                otherServiceDescription: formData.otherServiceDescription,
                serviceAreaRadius: formData.serviceAreaRadius,
                offers24_7Emergency: formData.offers24_7Emergency,
                emergencyPhone: formData.emergencyPhone,
                paymentTerms: formData.paymentTerms,
                preferredPaymentMethod: formData.preferredPaymentMethod,
                notes: formData.notes,
            };

            if (vendor) {
                await vendorService.updateVendor(vendor.id, vendorData, currentUserId, currentUserName);
                toast.success('Vendor updated successfully');
                onSuccess();
                onClose();
            } else {
                await vendorService.createVendor(vendorData as any, currentUserId, currentUserName);
                toast.success('Vendor created successfully');
                setStep('invite');
            }
        } catch (error: any) {
            console.error('Failed to save vendor:', error);
            toast.error(error.message || 'Failed to save vendor');
        } finally {
            setLoading(false);
        }
    };

    const handleSendInvite = async () => {
        if (!formData.email) { toast.error('Email is required to send invite'); return; }
        try {
            setSendingInvite(true);
            const functions = getFunctions();
            const createVendorUser = httpsCallable(functions, 'createVendorUserWithMagicLink');
            const result = await createVendorUser({
                email: formData.email,
                displayName: formData.contactName || formData.name,
                companyName: formData.name,
                phone: formData.phone,
            });
            const data = result.data as { success: boolean; message?: string };
            if (data.success) {
                toast.success('Login credentials sent to vendor\'s email');
                onSuccess(); onClose();
            } else {
                toast.error(data.message || 'Failed to send invite');
            }
        } catch (error: any) {
            toast.success('Vendor created! Email invite feature coming soon.');
            onSuccess(); onClose();
        } finally {
            setSendingInvite(false);
        }
    };

    const handleSkipInvite = () => { onSuccess(); onClose(); };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className={cn(
                "sm:max-w-[700px] max-h-[90vh] overflow-y-auto p-0 gap-0 border rounded-xl",
                isDarkMode ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200"
            )}>
                {/* Header */}
                <div className={cn(
                    "px-6 py-5 border-b flex items-center justify-between sticky top-0 z-10",
                    isDarkMode ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200"
                )}>
                    <div className="flex items-center gap-3">
                        <div className={cn("p-2.5 rounded-lg", isDarkMode ? "bg-purple-500/20" : "bg-purple-100")}>
                            <Building2 className={cn("w-5 h-5", isDarkMode ? "text-purple-400" : "text-purple-600")} />
                        </div>
                        <div>
                            <h2 className={cn("text-lg font-bold", isDarkMode ? "text-white" : "text-gray-900")}>
                                {step === 'invite' ? 'Send Login Credentials' : (vendor ? 'Edit Vendor' : 'Add New Vendor')}
                            </h2>
                            <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-gray-500")}>
                                {step === 'invite' ? 'Vendor can log in to manage their profile' : 'Comprehensive vendor profile'}
                            </p>
                        </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={onClose}
                        className={cn("h-9 w-9 rounded-lg", isDarkMode ? "hover:bg-slate-700 text-slate-400" : "hover:bg-gray-100 text-gray-500")}>
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                {step === 'invite' ? (
                    <div className="p-6 space-y-6">
                        <div className={cn("p-4 rounded-lg border", isDarkMode ? "bg-green-900/20 border-green-800" : "bg-green-50 border-green-200")}>
                            <div className="flex items-center gap-3">
                                <div className={cn("p-2 rounded-full", isDarkMode ? "bg-green-500/20" : "bg-green-100")}>
                                    <Shield className="w-5 h-5 text-green-500" />
                                </div>
                                <div>
                                    <p className={cn("font-semibold", isDarkMode ? "text-green-400" : "text-green-700")}>Vendor Created Successfully!</p>
                                    <p className={cn("text-sm", isDarkMode ? "text-green-300/70" : "text-green-600")}>
                                        Would you like to send login credentials to {formData.email}?
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className={cn("p-4 rounded-lg border", isDarkMode ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200")}>
                            <div className="flex items-start gap-3">
                                <Key className={cn("w-5 h-5 mt-0.5", isDarkMode ? "text-purple-400" : "text-purple-600")} />
                                <div>
                                    <p className={cn("font-medium mb-2", isDarkMode ? "text-white" : "text-gray-900")}>What happens when you send an invite:</p>
                                    <ul className={cn("text-sm space-y-1", isDarkMode ? "text-slate-400" : "text-gray-600")}>
                                        <li>• Vendor receives a magic link via email</li>
                                        <li>• They can set up their password securely</li>
                                        <li>• Full access to their vendor portal</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={handleSkipInvite}
                                className={cn("flex-1 rounded-lg h-11", isDarkMode ? "border-slate-600 hover:bg-slate-700" : "border-gray-300 hover:bg-gray-50")}>
                                Skip for Now
                            </Button>
                            <Button onClick={handleSendInvite} disabled={sendingInvite}
                                className="flex-1 rounded-lg h-11 bg-purple-600 hover:bg-purple-700 text-white">
                                {sendingInvite ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Login Invite</>}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {/* Company Info */}
                        <div className="space-y-4">
                            <h3 className={cn("text-sm font-semibold uppercase tracking-wide flex items-center gap-2", isDarkMode ? "text-slate-400" : "text-gray-500")}>
                                <Building2 className="w-4 h-4" />Company Information
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className={isDarkMode ? "text-white" : "text-gray-900"}>Company Name <span className="text-red-500">*</span></Label>
                                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="ABC Services LLC" required
                                        className={cn("rounded-lg h-10", isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300")} />
                                </div>
                                <div className="space-y-2">
                                    <Label className={isDarkMode ? "text-white" : "text-gray-900"}>DBA Name</Label>
                                    <Input value={formData.dbaName} onChange={(e) => setFormData({ ...formData, dbaName: e.target.value })} placeholder="Doing business as..."
                                        className={cn("rounded-lg h-10", isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300")} />
                                </div>
                            </div>
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-4">
                            <h3 className={cn("text-sm font-semibold uppercase tracking-wide flex items-center gap-2", isDarkMode ? "text-slate-400" : "text-gray-500")}>
                                <User className="w-4 h-4" />Contact Information
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className={isDarkMode ? "text-white" : "text-gray-900"}>Contact Name</Label>
                                    <Input value={formData.contactName} onChange={(e) => setFormData({ ...formData, contactName: e.target.value })} placeholder="John Smith"
                                        className={cn("rounded-lg h-10", isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300")} />
                                </div>
                                <div className="space-y-2">
                                    <Label className={isDarkMode ? "text-white" : "text-gray-900"}>Title</Label>
                                    <Input value={formData.contactTitle} onChange={(e) => setFormData({ ...formData, contactTitle: e.target.value })} placeholder="Operations Manager"
                                        className={cn("rounded-lg h-10", isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300")} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className={isDarkMode ? "text-white" : "text-gray-900"}>Email <span className="text-red-500">*</span></Label>
                                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="vendor@example.com" required
                                        className={cn("rounded-lg h-10", isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300")} />
                                </div>
                                <div className="space-y-2">
                                    <Label className={isDarkMode ? "text-white" : "text-gray-900"}>Phone</Label>
                                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(555) 123-4567"
                                        className={cn("rounded-lg h-10", isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300")} />
                                </div>
                            </div>
                        </div>

                        {/* Address */}
                        <div className="space-y-4">
                            <h3 className={cn("text-sm font-semibold uppercase tracking-wide flex items-center gap-2", isDarkMode ? "text-slate-400" : "text-gray-500")}>
                                <MapPin className="w-4 h-4" />Physical Address
                            </h3>
                            <Input value={formData.street} onChange={(e) => setFormData({ ...formData, street: e.target.value })} placeholder="Street Address"
                                className={cn("rounded-lg h-10", isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300")} />
                            <div className="grid grid-cols-3 gap-4">
                                <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="City"
                                    className={cn("rounded-lg h-10", isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300")} />
                                <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} placeholder="State"
                                    className={cn("rounded-lg h-10", isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300")} />
                                <Input value={formData.zip} onChange={(e) => setFormData({ ...formData, zip: e.target.value })} placeholder="ZIP"
                                    className={cn("rounded-lg h-10", isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300")} />
                            </div>
                        </div>

                        {/* Services */}
                        <div className="space-y-4">
                            <h3 className={cn("text-sm font-semibold uppercase tracking-wide flex items-center gap-2", isDarkMode ? "text-slate-400" : "text-gray-500")}>
                                <Wrench className="w-4 h-4" />Services Offered
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {serviceOptions.map(service => (
                                    <button key={service} type="button" onClick={() => handleServiceToggle(service)}
                                        className={cn("px-3 py-1.5 text-sm rounded-full border transition-all",
                                            formData.services.includes(service)
                                                ? "bg-purple-600 text-white border-purple-600"
                                                : isDarkMode ? "bg-slate-800 border-slate-600 text-slate-300 hover:border-purple-500" : "bg-white border-gray-300 text-gray-700 hover:border-purple-400"
                                        )}>
                                        {service}
                                    </button>
                                ))}
                            </div>
                            <label className={cn("flex items-center gap-2 cursor-pointer", isDarkMode ? "text-white" : "text-gray-900")}>
                                <input type="checkbox" checked={formData.offers24_7Emergency} onChange={(e) => setFormData({ ...formData, offers24_7Emergency: e.target.checked })}
                                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                                <span className="text-sm">Offers 24/7 Emergency Service</span>
                            </label>
                        </div>

                        {/* Payment */}
                        <div className="space-y-4">
                            <h3 className={cn("text-sm font-semibold uppercase tracking-wide flex items-center gap-2", isDarkMode ? "text-slate-400" : "text-gray-500")}>
                                <CreditCard className="w-4 h-4" />Payment Preferences
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className={isDarkMode ? "text-white" : "text-gray-900"}>Payment Terms</Label>
                                    <select value={formData.paymentTerms} onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value as any })}
                                        className={cn("w-full rounded-lg border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500",
                                            isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900")}>
                                        <option value="due_on_receipt">Due on Receipt</option>
                                        <option value="net_15">Net 15</option>
                                        <option value="net_30">Net 30</option>
                                        <option value="net_45">Net 45</option>
                                        <option value="net_60">Net 60</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label className={isDarkMode ? "text-white" : "text-gray-900"}>Preferred Payment Method</Label>
                                    <select value={formData.preferredPaymentMethod} onChange={(e) => setFormData({ ...formData, preferredPaymentMethod: e.target.value as any })}
                                        className={cn("w-full rounded-lg border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500",
                                            isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300 text-gray-900")}>
                                        <option value="check">Check</option>
                                        <option value="ach">ACH Transfer</option>
                                        <option value="wire">Wire Transfer</option>
                                        <option value="credit_card">Credit Card</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <Label className={isDarkMode ? "text-white" : "text-gray-900"}>Internal Notes</Label>
                            <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any notes about this vendor..."
                                className={cn("min-h-[80px] rounded-lg resize-none", isDarkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-gray-300")} />
                        </div>

                        {/* Actions */}
                        <div className={cn("flex justify-end gap-3 pt-4 border-t", isDarkMode ? "border-slate-700" : "border-gray-200")}>
                            <Button type="button" variant="outline" onClick={onClose}
                                className={cn("rounded-lg px-5", isDarkMode ? "border-slate-600 hover:bg-slate-700 text-slate-300" : "border-gray-300 hover:bg-gray-50")}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={loading} className="rounded-lg px-5 bg-purple-600 hover:bg-purple-700 text-white">
                                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : (vendor ? 'Update Vendor' : 'Create Vendor')}
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
